// A token's candles: the bars so far from GET /tokens/:chain/:address/candles, then the bar in
// progress and each closing bar from the candles channel, applied by their start time. The API's
// candles start at 1m: 1s and 5s bars are built here from the chart pool's trades (GET /trades and
// the trades channel) by the candles' own rules, so they reach back only as far as those trades.
import { CandlestickSeries, ColorType, createChart, CrosshairMode, HistogramSeries, TickMarkType, type IChartApi, type ISeriesApi, type UTCTimestamp } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { get, useLive, type Bar, type ChainId, type Trade } from "./api";
import { price, usd } from "./format";

const RESOLUTIONS = ["1s", "5s", "1m", "5m", "15m", "1h", "4h", "1d"] as const;
type Res = (typeof RESOLUTIONS)[number];
const FROM_TRADES: Partial<Record<Res, number>> = { "1s": 1, "5s": 5 };
const TRADE_PAGES = 2; // of 200 trades: the history a 1s or 5s chart starts with

const tradeKey = (t: Trade) => `${t.tx}:${t.eventIndex}`;
const oldest = (a: Trade, b: Trade) => a.time - b.time || a.block - b.block || a.txIndex - b.txIndex || a.eventIndex - b.eventIndex;

/** Bars of `step` seconds from a pool's trades, as the API builds its candles: priced swaps worth
 *  $0.01 or more set the price, and each bar opens at the previous bar's close. */
export function barsFromTrades(trades: Iterable<Trade>, step: number): Bar[] {
  const out: Bar[] = [];
  let bar = null as Bar | null;
  for (const t of [...trades].filter((t) => t.priceUsd != null && (t.amountUsd ?? 0) >= 0.01).sort(oldest)) {
    const at = Math.floor(t.time / 1000 / step) * step;
    const p = t.priceUsd!;
    if (bar?.t !== at) {
      const o: number = bar ? bar.c : p;
      bar = { t: at, o, h: o, l: o, c: o, v: 0, n: 0 };
      out.push(bar);
    }
    bar.h = Math.max(bar.h, p);
    bar.l = Math.min(bar.l, p);
    bar.c = p;
    bar.v += t.amountUsd ?? 0;
    bar.n += 1;
  }
  return out;
}

const UP = "#00d26a";
const DOWN = "#ff3b4e";
const two = (n: number) => String(n).padStart(2, "0");

/** Time-axis marks in the viewer's own time: bars are aligned in UTC. */
export const localTicks = (t: number, type: TickMarkType) => {
      const d = new Date(t * 1000);
      if (type === TickMarkType.Year) return String(d.getFullYear());
      if (type === TickMarkType.Month) return d.toLocaleString(undefined, { month: "short" });
      if (type === TickMarkType.DayOfMonth) return String(d.getDate());
      if (type === TickMarkType.TimeWithSeconds) return `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
      return `${two(d.getHours())}:${two(d.getMinutes())}`;
};

export function Chart({ chain, token, pool, supply }: { chain: ChainId; token: string; pool: string | null; supply: number | null }) {
  const [res, setRes] = useState<Res>("1m");
  const [unit, setUnit] = useState<"price" | "mcap">("price");
  const mcap = unit === "mcap" && supply != null;
  // Market cap is the bars' price times the circulating supply, as the token page has it: the chart
  // draws through this factor, so the bars themselves stay prices and the toggle reloads nothing.
  const factor = useRef(1);
  factor.current = mcap ? supply : 1;
  const fmt = (p: number) => (factor.current === 1 ? price(p, "") : usd(p));
  const [status, setStatus] = useState<"loading" | "ready" | "empty" | "error">("loading");
  const el = useRef<HTMLDivElement>(null);
  const api = useRef<{ chart: IChartApi; candles: ISeriesApi<"Candlestick">; volume: ISeriesApi<"Histogram"> } | null>(null);
  const bars = useRef(new Map<number, Bar>());
  const trades = useRef(new Map<string, Trade>());
  const loaded = useRef(false);
  // Each candle size loads under its own generation: an answer for the previous one is dropped.
  const gen = useRef(0);
  const [since, setSince] = useState<number | null>(null);
  const step = pool ? FROM_TRADES[res] : undefined;

  useEffect(() => {
    const chart = createChart(el.current!, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#94a3b8",
        fontFamily: "'Instrument Sans Variable', system-ui, sans-serif",
        fontSize: 12,
      },
      grid: { vertLines: { color: "#0d0f13" }, horzLines: { color: "#0d0f13" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.08, bottom: 0.24 } },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        // Bars are aligned in UTC; the axis reads in the viewer's own time.
        tickMarkFormatter: localTicks,
      },
      localization: {
        priceFormatter: fmt,
        timeFormatter: (t: number) => new Date(t * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: t % 60 ? "2-digit" : undefined }),
      },
    });
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
      borderVisible: false,
      priceFormat: { type: "custom", formatter: fmt, minMove: 1e-12 },
    });
    const volume = chart.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    api.current = { chart, candles, volume };
    return () => {
      chart.remove();
      api.current = null;
    };
  }, []);

  const vol = (b: Bar) => ({ time: b.t as UTCTimestamp, value: b.v, color: b.c >= b.o ? "rgba(0,210,106,0.35)" : "rgba(255,59,78,0.35)" });
  const candle = (b: Bar) => {
    const f = factor.current;
    return { time: b.t as UTCTimestamp, open: b.o * f, high: b.h * f, low: b.l * f, close: b.c * f };
  };

  const draw = (fit: boolean) => {
    const a = api.current;
    if (!a) return;
    const list = [...bars.current.values()].sort((x, y) => x.t - y.t);
    a.candles.setData(list.map(candle));
    a.volume.setData(list.map(vol));
    if (fit && list.length) a.chart.timeScale().setVisibleLogicalRange({ from: Math.max(0, list.length - 140), to: list.length + 4 });
    setStatus(list.length ? "ready" : "empty");
  };

  const fromTrades = (fit: boolean) => {
    const list = barsFromTrades(trades.current.values(), step!);
    bars.current = new Map(list.map((b) => [b.t, b]));
    setSince(list[0] ? list[0].t * 1000 : null);
    draw(fit);
  };

  const load = async (fit: boolean) => {
    const mine = gen.current;
    try {
      if (step) {
        const found: Trade[] = [];
        let cursor: string | null = null;
        for (let page = 0; page < TRADE_PAGES; page++) {
          const r: { trades: Trade[]; next: string | null } = await get(`/trades?chain=${chain}&pool=${pool}&limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`);
          found.push(...r.trades);
          cursor = r.next;
          if (!cursor) break;
        }
        if (mine !== gen.current) return;
        for (const t of found) trades.current.set(tradeKey(t), t);
        loaded.current = true;
        fromTrades(fit);
      } else {
        const r = await get<{ bars: Bar[] }>(`/tokens/${chain}/${token}/candles?res=${res}`);
        if (mine !== gen.current) return;
        for (const b of r.bars) bars.current.set(b.t, b);
        loaded.current = true;
        draw(fit);
      }
    } catch {
      if (mine === gen.current && !loaded.current) setStatus("error");
    }
  };

  useEffect(() => api.current?.chart.timeScale().applyOptions({ secondsVisible: !!step }), [step]);

  useEffect(() => {
    if (loaded.current) draw(false);
  }, [mcap, supply]);

  useEffect(() => {
    gen.current += 1;
    bars.current.clear();
    trades.current.clear();
    loaded.current = false;
    setSince(null);
    setStatus("loading");
    draw(false);
    load(true);
  }, [chain, token, res, pool]);

  // 1s and 5s: the chart pool's trades as they are indexed, folded into the bars.
  useLive(step ? { channel: "trades", chain, pool: pool! } : null, (m) => {
    if (m.type === "subscribed" || m.type === "resubscribed") {
      if (loaded.current) load(false);
    } else if (m.type === "trades" && loaded.current) {
      for (const t of m.data as Trade[]) trades.current.set(tradeKey(t), t);
      if (trades.current.size > 5000) {
        const keep = [...trades.current.values()].sort(oldest).slice(-4000);
        trades.current = new Map(keep.map((t) => [tradeKey(t), t]));
      }
      fromTrades(false);
    } else if (m.type === "retract" && loaded.current) {
      for (const t of m.data as Trade[]) if (trades.current.get(tradeKey(t))?.block === t.block) trades.current.delete(tradeKey(t));
      fromTrades(false);
    }
  });

  useLive(step ? null : { channel: "candles", chain, token, res }, (m) => {
    if (m.type === "subscribed" || m.type === "resubscribed") {
      if (loaded.current) load(false);
    } else if (m.type === "candle") {
      const b = m.data as Bar;
      const last = Math.max(0, ...bars.current.keys());
      bars.current.set(b.t, b);
      const a = api.current;
      if (!loaded.current || !a) return;
      if (b.t >= last) {
        a.candles.update(candle(b));
        a.volume.update(vol(b));
        if (status !== "ready") setStatus("ready");
      } else draw(false);
    }
  });

  return (
    <div className="chart">
      <div className="chart-toolbar">
        <div className="segmented small" role="group" aria-label="Candle size">
          {RESOLUTIONS.map((r) => (
            <button
              key={r}
              className={r === res ? "seg active" : "seg"}
              onClick={() => setRes(r)}
              disabled={!pool && !!FROM_TRADES[r]}
              title={FROM_TRADES[r] ? "Built in the page from the chart pool's trades: the API's candles start at 1m" : undefined}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="segmented small" role="group" aria-label="The chart shows">
          <button className={mcap ? "seg" : "seg active"} onClick={() => setUnit("price")}>
            Price
          </button>
          <button className={mcap ? "seg active" : "seg"} onClick={() => setUnit("mcap")} disabled={supply == null} title={supply == null ? "This token's market cap is not known" : "Market cap: price times the circulating supply now"}>
            MCap
          </button>
        </div>
        <span className={step || mcap ? "muted chart-note" : "muted chart-note hide-sm"}>
          {step
            ? `${res} bars built from the pool's trades${since ? ` since ${new Date(since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}: the API's candles start at 1m`
            : mcap
              ? "Market cap: each price times the circulating supply now"
              : "Price in USD, from the pool that trades it most"}
        </span>
      </div>
      <div className="chart-canvas">
        <div className="chart-host" ref={el} />
        {status !== "ready" && (
          <p className="chart-status muted">
            {status === "loading" ? "Loading the chart…" : status === "empty" ? "No priced trades at this candle size yet." : "The chart could not be loaded."}
          </p>
        )}
      </div>
    </div>
  );
}
